import React, { useState, useRef } from "react";
import { App } from "antd";
import axios from "axios";
import "./QRLink.css";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { QRCodeCanvas } from "qrcode.react";
import {
  LinkOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";

const QR_COLORS = ["#000000", "#1a1a2e", "#e5322d", "#0891b2", "#1a7a43", "#6366f1"];
const QR_SIZES  = [
  { label: "Small",  value: 180 },
  { label: "Medium", value: 250 },
  { label: "Large",  value: 320 },
];

const QRLink = () => {
  const { message } = App.useApp();
  const [name,      setName]      = useState("");
  const [link,      setLink]      = useState("");
  const [qrUrl,     setQrUrl]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [qrColor,   setQrColor]   = useState("#000000");
  const [qrSize,    setQrSize]    = useState(250);
  const canvasWrapRef = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  // ── API — unchanged logic ──────────────────────────────────────────
  const generateQR = async () => {
    if (!link.trim()) { message.warning("Please enter a website URL."); return; }
    if (!name.trim()) { message.warning("Please give your QR a name."); return; }

    setLoading(true);
    const authToken = localStorage.getItem("token");

    try {
      const response = await axios.post(
        isModule
          ? `${process.env.REACT_APP_BASE_URL}/api/QR/link-to-qr`
          : `${process.env.REACT_APP_BASE_URL}/api/WebsiteQr/website-link-to-qr`,
        {
          link:          link,
          title:         name,
          access_source: isModule ? "Private-Web" : "Public-Web",
          qrtext:        "",
        },
        {
          headers: isModule ? { Authorization: `Bearer ${authToken}` } : {},
        }
      );

      if (response.data.error === false) {
        setQrUrl(link);
        message.success("QR Code generated!");
      } else {
        message.error(response.data.message || "Failed to generate QR code.");
      }
    } catch {
      message.error("Failed to generate QR code.");
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const pngUrl   = canvas.toDataURL("image/png");
    const anchor   = document.createElement("a");
    anchor.download = `${name.replace(/\s+/g, "-").toLowerCase() || "qr-code"}.png`;
    anchor.href    = pngUrl;
    anchor.click();
  };

  const reset = () => { setName(""); setLink(""); setQrUrl(""); };

  const handleKeyDown = (e) => { if (e.key === "Enter") generateQR(); };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`qrl-root ${isModule ? "qrl-module" : ""}`}>
        <div className="qrl-inner">

          {/* ── Left: form ── */}
          <div className="qrl-form-side">

            {/* Page heading */}
            <div className="qrl-heading">
              <div className="qrl-heading-icon">
                <LinkOutlined />
              </div>
              <div>
                <h1 className="qrl-title">Link to QR Code</h1>
                <p className="qrl-subtitle">
                  Turn any URL into a scannable QR code instantly.
                </p>
              </div>
            </div>

            <div className="qrl-card">

              <div className="qrl-field">
                <label className="qrl-label">QR Code Name</label>
                <input
                  className="qrl-input"
                  placeholder="e.g. My Portfolio"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              <div className="qrl-field">
                <label className="qrl-label">Website URL</label>
                <div className="qrl-input-wrap">
                  <LinkOutlined className="qrl-input-icon" />
                  <input
                    className="qrl-input qrl-input-icon-pad"
                    placeholder="https://www.yourwebsite.com"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>

              {/* Customisation */}
              <div className="qrl-divider" />

              <div className="qrl-field">
                <label className="qrl-label">QR Color</label>
                <div className="qrl-color-row">
                  {QR_COLORS.map((c) => (
                    <button
                      key={c}
                      className={`qrl-color-swatch ${qrColor === c ? "active" : ""}`}
                      style={{ background: c }}
                      onClick={() => setQrColor(c)}
                      title={c}
                    />
                  ))}
                  <input
                    type="color"
                    className="qrl-color-custom"
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    title="Custom color"
                  />
                </div>
              </div>

              <div className="qrl-field">
                <label className="qrl-label">QR Size</label>
                <div className="qrl-size-row">
                  {QR_SIZES.map((s) => (
                    <button
                      key={s.value}
                      className={`qrl-size-btn ${qrSize === s.value ? "active" : ""}`}
                      onClick={() => setQrSize(s.value)}
                    >
                      {s.label}
                      <span>{s.value}px</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="qrl-actions">
                <button
                  className="qrl-generate-btn"
                  onClick={generateQR}
                  disabled={loading}
                >
                  {loading
                    ? <span className="qrl-spinner" />
                    : <QrcodeOutlined />
                  }
                  {loading ? "Generating…" : "Generate QR Code"}
                </button>

                {qrUrl && (
                  <button className="qrl-reset-btn" onClick={reset}>
                    <ReloadOutlined /> Start over
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Right: preview ── */}
          <div className="qrl-preview-side">
            <div className="qrl-preview-card">

              <div className="qrl-preview-label">QR Preview</div>

              {qrUrl ? (
                <>
                  <div className="qrl-qr-wrap" ref={canvasWrapRef}>
                    <QRCodeCanvas
                      value={qrUrl}
                      size={qrSize}
                      fgColor={qrColor}
                      bgColor="#ffffff"
                      level="H"
                      includeMargin={true}
                    />
                  </div>

                  <div className="qrl-preview-success">
                    <CheckCircleFilled className="qrl-success-icon" />
                    <span>QR code ready!</span>
                  </div>

                  <div className="qrl-preview-name">{name}</div>

                  <div className="qrl-preview-url">
                    <LinkOutlined />
                    <span>{qrUrl.length > 40 ? qrUrl.slice(0, 40) + "…" : qrUrl}</span>
                  </div>

                  <button className="qrl-download-btn" onClick={downloadQR}>
                    <DownloadOutlined /> Download PNG
                  </button>
                </>
              ) : (
                <div className="qrl-placeholder">
                  <div className="qrl-placeholder-icon">
                    <QrcodeOutlined />
                  </div>
                  <div className="qrl-placeholder-text">
                    Fill in the form and click<br />
                    <strong>Generate QR Code</strong>
                  </div>
                </div>
              )}
            </div>

            {/* Tips card */}
            <div className="qrl-tips-card">
              <div className="qrl-tips-title">💡 Tips</div>
              <ul className="qrl-tips-list">
                <li>Always include <code>https://</code> in your URL</li>
                <li>Use <strong>Large</strong> size for print materials</li>
                <li>Dark colors work best for scanning</li>
                <li>Test your QR before printing</li>
              </ul>
            </div>
          </div>

        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default QRLink;